// @ts-nocheck
/* ============================================================
   Fence Works — parcel resolver (LIVE)
   Queries free, keyless ArcGIS REST FeatureServers for the real
   parcel polygon under a clicked point. Both providers are ArcGIS
   FeatureServers with an IDENTICAL query format — only the base URL
   changes. Address search uses the free US Census geocoder.
   Cached/synth parcels remain ONLY as an offline fallback.

   Returns: { parcelId, owner, address, provider, providerStatus,
             center:[lat,lng], polygon:[[lat,lng],...], live:bool }
   ============================================================ */
(function () {
  "use strict";

  // ---- statewide parcel layers (free, keyless ArcGIS) ----
  // Adding a state = one entry: its hosted statewide parcel layer + a rough
  // bbox so the right layer is picked from the searched/clicked point.
  const STATE_LAYERS = [
    { key: "wi", label: "Wisconsin Statewide Parcels",
      // Wisconsin_Statewide_Parcels_DB = the SCO's PUBLIC FeatureServer whose
      // layer 0 is the current yearly parcel view (V1200_…_2026 today). The
      // ORIGINAL item name (Wisconsin_Statewide_Parcels, no _DB) went
      // token-gated in 2026 and broke live lookups; the _DB service is the
      // stable, anonymous one — no year in its name, the yearly layer swaps
      // underneath it. Verified against the live services directory. A custom
      // override (Settings → Parcel Data Source, config.statewideUrl) still
      // takes priority if this ever changes again.
      base: "https://services3.arcgis.com/n6uYoouQZW75n5WI/arcgis/rest/services/Wisconsin_Statewide_Parcels_DB/FeatureServer/0",
      alts: [],
      bbox: [42.4, -93.0, 47.4, -86.2] },   // [minLat, minLng, maxLat, maxLng]
    // FL = the FDOR statewide tax-roll cadastral (layer 0 = "FDOR Cadastral").
    // NOTE: the service is literally named *Cadastral*, not *Parcels* — the
    // Florida_Statewide_Parcels guess 400'd because it doesn't exist.
    { key: "fl", label: "Florida Statewide Parcels",
      base: "https://services9.arcgis.com/Gh9awoU677aKree0/arcgis/rest/services/Florida_Statewide_Cadastral/FeatureServer/0",
      bbox: [24.3, -87.7, 31.1, -79.9] },
  ];
  const WI_BASE = STATE_LAYERS[0].base;
  const STATE_NAMES = STATE_LAYERS.map(s => s.key.toUpperCase()).join(" & ");
  function statewideFor(lat, lng) {
    return STATE_LAYERS.find(s => lat >= s.bbox[0] && lat <= s.bbox[2] && lng >= s.bbox[1] && lng <= s.bbox[3]) || null;
  }
  const DEFAULT_COUNTY_URL = "https://geo.dot.gov/server/rest/services/hosted/2020_Brown_County_Parcels_ROW/FeatureServer/0";

  let config = { provider: "wi", countyUrl: DEFAULT_COUNTY_URL };   // "wi" = statewide-auto (legacy value)
  function setConfig(c) { config = Object.assign({}, config, c || {}); }
  function getConfig() { return Object.assign({}, config); }

  // ---- ArcGIS parcel-under-a-point query ----
  // Two access strategies, tried in order and remembered per layer:
  //   A) one-shot — a spatial intersects query that returns geometry+fields in
  //      a single call (geojson, then classic json). Works for WI Statewide,
  //      county FeatureServers, and any normal hosted layer.
  //   B) two-step — for *tile-optimized* hosted layers (the FL FDOR Cadastral
  //      runs in "tiles basic query" mode and rejects returnGeometry on a
  //      spatial query): first an ids-only spatial query to find the parcel
  //      under the point, then fetch THAT objectId with geometry. This is
  //      exactly how Esri's own map viewer identifies a parcel.
  function arcQuery(base, params) {
    return base.replace(/\/+$/, "") + "/query?" +
      Object.keys(params).map(k => k + "=" + encodeURIComponent(params[k])).join("&");
  }
  function ptParams(lat, lng, bufferMeters) {
    const p = { geometry: lng + "," + lat, geometryType: "esriGeometryPoint", inSR: 4326, spatialRel: "esriSpatialRelIntersects" };
    if (bufferMeters) { p.distance = bufferMeters; p.units = "esriSRUnit_Meter"; }
    return p;
  }
  function esriToFeatureCollection(j) {
    // Esri JSON → the GeoJSON-shaped FeatureCollection the rest of the file reads
    const feats = (j && j.features || []).map(f => {
      const rings = f.geometry && f.geometry.rings;
      if (!rings || !rings.length) return null;
      // as MultiPolygon so outerRing() picks the largest ring (Esri polygons
      // may pack several outer rings + holes into one rings array)
      return { geometry: { type: "MultiPolygon", coordinates: rings.map(r => [r]) }, properties: f.attributes || {} };
    }).filter(Boolean);
    return { type: "FeatureCollection", features: feats };
  }
  // every parcel-layer fetch gets a hard timeout — a layer that BLACKHOLES
  // (request stuck "pending" forever) used to hang the whole lookup and leave
  // the estimator spinning; now it aborts and the next source gets its turn.
  // resolveDeadline caps the WHOLE resolve() walk (all sources + retries).
  let resolveDeadline = 0;
  function timeLeft() { return resolveDeadline ? resolveDeadline - Date.now() : 8000; }
  async function fetchArc(url) {
    const left = timeLeft();
    if (left < 600) throw new Error("parcel lookup timed out");
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), Math.min(8000, left));
    try {
      const r = await fetch(url, { headers: { Accept: "application/json" }, signal: ctrl.signal });
      if (!r.ok) throw new Error("HTTP " + r.status);
      return await r.json();
    } catch (e) {
      throw (e && e.name === "AbortError") ? new Error("timed out (no answer in 8s)") : e;
    } finally { clearTimeout(t); }
  }
  // strategy A — geometry + fields in one spatial query (geojson then json)
  async function oneShot(base, lat, lng, bufferMeters) {
    const base4326 = Object.assign(ptParams(lat, lng, bufferMeters), { outFields: "*", returnGeometry: true, outSR: 4326 });
    for (const fmt of ["geojson", "json"]) {
      const j = await fetchArc(arcQuery(base, Object.assign({}, base4326, { f: fmt })));
      if (j && j.error) continue;                              // error envelope → next format
      if (fmt === "geojson" && j && j.type) return j;          // native GeoJSON FC
      if (j && j.features) return esriToFeatureCollection(j);  // Esri JSON
    }
    throw new Error("one-shot query rejected");
  }
  // strategy B — ids-only spatial query, then fetch the parcel by id w/ geometry
  async function twoStep(base, lat, lng, bufferMeters) {
    const idj = await fetchArc(arcQuery(base, Object.assign(ptParams(lat, lng, bufferMeters), { returnIdsOnly: true, f: "json" })));
    if (idj && idj.error) throw new Error((idj.error && idj.error.message) || "ids query error");
    const ids = (idj && idj.objectIds) || [];
    if (!ids.length) return { type: "FeatureCollection", features: [] };   // reached layer, no parcel
    const j2 = await fetchArc(arcQuery(base, { objectIds: ids.slice(0, 6).join(","), outFields: "*", returnGeometry: true, outSR: 4326, f: "json" }));
    if (j2 && j2.error) throw new Error((j2.error && j2.error.message) || "by-id query error");
    return esriToFeatureCollection(j2);
  }
  const strategyCache = {};   // base URL → "one" | "two" (remember the winner)
  async function queryArc(base, lat, lng, bufferMeters) {
    if (strategyCache[base] === "two") return twoStep(base, lat, lng, bufferMeters);
    try {
      const fc = await oneShot(base, lat, lng, bufferMeters);
      strategyCache[base] = "one";
      return fc;
    } catch (e) {
      const fc = await twoStep(base, lat, lng, bufferMeters);   // throws → resolve() falls back offline
      strategyCache[base] = "two";
      return fc;
    }
  }
  // the free statewide layers blip (rate limits, brief 5xx) — one spaced retry
  // rides most of them out instead of dropping the user onto a placeholder box
  const sleep = (ms) => new Promise(r => setTimeout(r, ms));
  async function queryArcRetry(base, lat, lng, bufferMeters) {
    try { return await queryArc(base, lat, lng, bufferMeters); }
    catch (e) {
      if (timeLeft() < 2500) throw e;   // out of budget — don't burn it on a retry
      await sleep(900);
      return queryArc(base, lat, lng, bufferMeters);
    }
  }

  // ---- geometry helpers ----
  function ringArea(ring) { // shoelace on [lng,lat]
    let a = 0;
    for (let i = 0, n = ring.length, j = n - 1; i < n; j = i++) a += (ring[j][0] + ring[i][0]) * (ring[j][1] - ring[i][1]);
    return a / 2;
  }
  function outerRing(geom) {
    if (!geom) return null;
    if (geom.type === "Polygon") return geom.coordinates[0];
    if (geom.type === "MultiPolygon") { // largest outer ring
      let best = null, bestA = -1;
      geom.coordinates.forEach(poly => { const a = Math.abs(ringArea(poly[0])); if (a > bestA) { bestA = a; best = poly[0]; } });
      return best;
    }
    return null;
  }
  function toLatLng(ring) { // [lng,lat] -> [lat,lng], strip closing dup
    let poly = ring.map(([x, y]) => [y, x]);
    if (poly.length > 2) {
      const a = poly[0], b = poly[poly.length - 1];
      if (Math.abs(a[0] - b[0]) < 1e-9 && Math.abs(a[1] - b[1]) < 1e-9) poly.pop();
    }
    return poly;
  }
  function centerOf(poly) {
    let minLat = 1e9, maxLat = -1e9, minLng = 1e9, maxLng = -1e9;
    poly.forEach(([la, ln]) => { minLat = Math.min(minLat, la); maxLat = Math.max(maxLat, la); minLng = Math.min(minLng, ln); maxLng = Math.max(maxLng, ln); });
    return [(minLat + maxLat) / 2, (minLng + maxLng) / 2];
  }

  // ---- field mapping (names vary by layer) ----
  function pick(props, exact, partial) {
    if (!props) return "";
    const keys = Object.keys(props);
    const val = (k) => (k && props[k] != null && String(props[k]).trim()) ? String(props[k]).trim() : "";
    for (const c of exact) { const k = keys.find(kk => kk.toLowerCase() === c.toLowerCase()); const v = val(k); if (v) return v; }
    for (const c of (partial || [])) { const k = keys.find(kk => kk.toLowerCase().includes(c.toLowerCase())); const v = val(k); if (v) return v; }
    return "";
  }
  function parseProps(props) {
    let address = pick(props,
      ["SITEADRESS_STAND", "SITEADRESS", "SITUSADDR", "PSTLADRESS", "PROPSTREET", "ADD_FULL", "FULLADDR", "ADDRESS", "PHY_ADDR1"],
      ["siteaddr", "situs", "propadd", "full_addr", "mailadd", "address", "phy_addr"]);
    if (!address) {
      const num = pick(props, ["ADD_NUM", "HOUSENO", "STNUM", "ADDRNO", "HOUSE"], ["housenum", "addr_num", "stnum"]);
      const street = pick(props, ["STREETNAME", "STREET", "ROADNAME", "ST_NAME"], ["streetname", "street", "road"]);
      if (street) address = (num ? num + " " : "") + street;
    }
    const owner = pick(props, ["OWNERNME1", "OWNER", "OWNERNAME", "OWNER1", "TAXPAYER", "DEEDHOLDER", "OWN_NAME"], ["ownernme", "owner", "own_name", "taxname", "deed"]);
    const parcelId = pick(props, ["PARCELID", "PIN", "PARCELNUMB", "PARNO", "TAXPARCELID", "MAPNO", "PARCEL_ID"], ["parcelid", "parcelnum", "parno", "pin", "apn"]);
    const county = pick(props, ["CONAME", "CO_NAME", "COUNTY", "CNTYNAME", "CNTY_NAME"], ["coname", "county", "cnty"]);
    return { address, owner, parcelId, county };
  }

  // ---- geocoding (free, keyless) ----
  // The US Census geocoder is the most accurate for street addresses but
  // sends NO CORS headers, so calling it from the browser guarantees a red
  // CORS error in the console before the fallback kicks in. Our API proxies
  // it server-side (/geo/geocode: Census → Nominatim); the browser only
  // talks to Nominatim directly when no API is configured (preview mode).
  function apiBase() {
    // the sandbox removes VFP_API_BASE (no paid calls) but keeps the keyless
    // geocoder proxy reachable through its own base
    const demo: any = (window as any).VFP_DEMO;
    const b = String(window.VFP_API_BASE || (demo && demo.active && demo.apiBase) || "").replace(/\/$/, "");
    return b || null;
  }
  // plain fetch with an abort timeout — geocoders can blackhole too
  async function fetchT(url, ms) {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), ms || 9000);
    try { return await fetch(url, { signal: ctrl.signal }); } finally { clearTimeout(t); }
  }
  // sandbox sessions tag their geocoder calls so the server can budget them
  // per session (a query param, not a header — no CORS preflight)
  function demoQs() {
    const d: any = (window as any).VFP_DEMO;
    return d && d.active && d.sessionId ? "&ds=" + encodeURIComponent(d.sessionId) : "";
  }
  async function geocodeServer(address) {
    const b = apiBase();
    if (!b) return null;
    const r = await fetchT(b + "/geo/geocode?q=" + encodeURIComponent(address) + demoQs(), 9000);
    if (r.status === 404) return null;                  // reachable, no match
    if (!r.ok) throw new Error("geo HTTP " + r.status);
    const j = await r.json();
    return (j && isFinite(j.lat) && isFinite(j.lng)) ? j : null;
  }
  async function geocodeNominatim(address) {
    const url = "https://nominatim.openstreetmap.org/search?format=json&limit=1&q=" + encodeURIComponent(address);
    const r = await fetchT(url, 9000);
    if (!r.ok) throw new Error("nominatim HTTP " + r.status);
    const j = await r.json();
    if (!j || !j[0]) return null;
    return { lat: +j[0].lat, lng: +j[0].lon, address: j[0].display_name.split(",").slice(0, 4).join(",").trim(), via: "osm" };
  }
  async function geocode(address) {
    try { const s = await geocodeServer(address); if (s) return s; } catch (e) { /* server down → direct */ }
    try { return await geocodeNominatim(address); } catch (e) { return null; }
  }

  // ---- autocomplete suggestions (proxied when the API is up; Nominatim direct otherwise) ----
  async function suggest(query) {
    if (!query || query.trim().length < 3) return [];
    const b = apiBase();
    const url = b
      ? b + "/geo/suggest?q=" + encodeURIComponent(query) + demoQs()
      : "https://nominatim.openstreetmap.org/search?format=jsonv2&limit=6&addressdetails=1&countrycodes=us&q=" + encodeURIComponent(query);
    const r = await fetchT(url, 9000);
    if (!r.ok) return [];
    const j = await r.json();
    return (j || []).map(m => {
      const a = m.address || {};
      const houseLine = [a.house_number, a.road].filter(Boolean).join(" ") || (m.name || m.display_name.split(",")[0]);
      const cityLine = [a.city || a.town || a.village || a.hamlet || a.county, a.state, a.postcode].filter(Boolean).join(", ");
      return { line1: houseLine, line2: cityLine || "United States", lat: +m.lat, lng: +m.lon, address: m.display_name.split(",").slice(0, 4).join(",").trim() };
    });
  }

  // ---- cached / synth fallback (offline only) ----
  const FT_PER_DEG_LAT = 364000;
  const SEEDS = [{
    parcelId: "5-621", owner: "DAVID DAHLKE", address: "312 N Oakland Ave, Green Bay, WI 54303",
    center: [44.521268, -88.024058],
    polygon: [
      [44.5212433589342, -88.0237871727447], [44.5211196216048, -88.0238698498176],
      [44.5211798678449, -88.0240423316103], [44.5212160154449, -88.0241458196024],
      [44.5212280646535, -88.0241803173055], [44.5212762603993, -88.0243183032633],
      [44.5213999090052, -88.0242353706878],
    ],
  }];
  function dist(a, b) { const dy = (a[0] - b[0]) * 111320, dx = (a[1] - b[1]) * 111320 * Math.cos(a[0] * Math.PI / 180); return Math.hypot(dx, dy); }
  function synth(lat, lng) {
    const cos = Math.cos(lat * Math.PI / 180);
    const ftLat = (ft) => ft / FT_PER_DEG_LAT, ftLng = (ft) => ft / (FT_PER_DEG_LAT * cos);
    const seed = Math.abs(Math.sin(lat * 91.7 + lng * 47.3));
    const widthFt = 48 + Math.round(seed * 24), depthFt = 110 + Math.round(seed * 50), theta = (seed - 0.5) * 0.7;
    const hw = widthFt / 2, hd = depthFt / 2;
    const polygon = [[-hw, hd], [hw, hd], [hw, -hd], [-hw, -hd]].map(([x, y]) => {
      const xr = x * Math.cos(theta) - y * Math.sin(theta), yr = x * Math.sin(theta) + y * Math.cos(theta);
      return [lat + ftLat(yr), lng + ftLng(xr)];
    });
    // owner is "" on purpose: a synthesized outline must never carry a made-up
    // homeowner name that could prefill a real customer record
    return { parcelId: "EST-" + (600 + Math.round(seed * 99)), owner: "", address: "", center: [lat, lng], polygon };
  }
  function fallback(lat, lng, address) {
    const hit = SEEDS.find(s => dist([lat, lng], s.center) < 220);
    const base = hit ? Object.assign({}, hit) : synth(lat, lng);
    if (address && !base.address) base.address = address;
    // queryPoint/queryAddress: what the user actually asked for, so the
    // estimator's "Retry live lookup" can re-run the exact same resolve
    return Object.assign(base, { provider: "Sample / cached parcel", providerStatus: "sample", live: false, queryPoint: [lat, lng], queryAddress: address || null });
  }

  // ---- main resolve ----
  function buildResult(feat, address, label, statusKey, lat, lng) {
    const ring = feat && outerRing(feat.geometry);
    if (!ring || ring.length < 3) return null;
    const polygon = toLatLng(ring);
    const meta = parseProps(feat.properties || {});
    return {
      parcelId: meta.parcelId || "—",
      owner: meta.owner || "—",
      address: meta.address || address || "",
      provider: label,
      providerStatus: statusKey,
      county: meta.county || null,   // → permit-by-county on the work order
      live: true,
      center: centerOf(polygon),
      polygon,
      queryPoint: lat != null ? [lat, lng] : null,
      queryAddress: address || null,
    };
  }
  // leading house number of an address string ("2151 Summer Breeze Ct" → "2151")
  function houseNumOf(addr) { const m = String(addr || "").trim().match(/^#?\s*(\d{1,6})\b/); return m ? m[1] : ""; }
  function nearestFeature(features, lat, lng) {
    let best = null, bestD = Infinity;
    features.forEach(f => {
      const ring = outerRing(f.geometry); if (!ring) return;
      const c = centerOf(toLatLng(ring));
      const d = dist([lat, lng], c);
      if (d < bestD) { bestD = d; best = f; }
    });
    return best;
  }
  // full two-pass lookup against ONE layer; returns a result, null (layer
  // answered but has no parcel there), or throws (layer unreachable/rejected)
  async function lookupAt(base, lat, lng, address, wantNum, label, statusKey) {
    // pass 1: exact point-in-polygon (with one spaced retry — blips are common)
    let fc = await queryArcRetry(base, lat, lng);
    let feat = fc && fc.features && fc.features[0];
    // address-aware correction: geocoders drop cul-de-sac / interpolated points
    // a lot or two off, so the point lands inside the WRONG parcel. When we know
    // the searched house number and the hit doesn't match it, search a small
    // radius and prefer the parcel whose street number actually matches.
    if (wantNum) {
      const featNum = (feat && outerRing(feat.geometry)) ? houseNumOf(parseProps(feat.properties || {}).address) : "";
      if (featNum !== wantNum) {
        try {
          const fcB = await queryArc(base, lat, lng, 60);
          const cands = (fcB && fcB.features) || [];
          const match = cands.find(f => outerRing(f.geometry) && houseNumOf(parseProps(f.properties || {}).address) === wantNum);
          if (match) feat = match;
          else if ((!feat || !outerRing(feat.geometry)) && cands.length) feat = nearestFeature(cands, lat, lng);
        } catch (e) { /* keep the pass-1 hit on error */ }
      }
    }
    // pass 2: address points often land in the road, buffer ~22m and take nearest parcel
    if (!feat || !outerRing(feat.geometry)) {
      fc = await queryArcRetry(base, lat, lng, 22);
      if (fc && fc.features && fc.features.length) feat = nearestFeature(fc.features, lat, lng);
    }
    return buildResult(feat, address, label, statusKey, lat, lng);
  }

  async function resolve(lat, lng, address) {
    resolveDeadline = Date.now() + 22000;   // whole walk (all sources + retries) answers in ≤22s
    let bases, label, statusKey;
    if (config.provider === "county" && config.countyUrl && config.countyUrl.trim()) {
      bases = [config.countyUrl.trim().replace(/\/+$/, "")];
      label = "County GIS / ArcGIS FeatureServer";
      statusKey = "county-live";
    } else {
      // statewide-auto: pick the state layer the point falls in
      const st = statewideFor(lat, lng);
      if (!st) {
        return Object.assign(fallback(lat, lng, address), {
          providerStatus: "none", noCoverage: true,
          provider: "Statewide parcels — this state isn't covered yet (" + STATE_NAMES + " so far)",
        });
      }
      // custom override (Settings) first, then the built-in default + backups
      const custom = String(config.statewideUrl || "").trim().replace(/\/+$/, "");
      bases = (custom ? [custom] : []).concat([st.base], st.alts || []).filter((b, i, a) => a.indexOf(b) === i);
      label = st.label; statusKey = st.key + "-live";
    }
    const wantNum = houseNumOf(address);   // the house number the user actually searched
    let lastErr = null, reachedEmpty = false, emptyLabel = label;
    for (let bi = 0; bi < bases.length; bi++) {
      const base = bases[bi];
      const lbl = bi === 0 ? label : label + " (backup source)";
      try {
        const result = await lookupAt(base, lat, lng, address, wantNum, lbl, statusKey);
        if (result) return result;
        reachedEmpty = true; emptyLabel = lbl;   // layer answered, no parcel — a backup may still have it
        try { console.warn("[parcel] reached " + lbl + " but no parcel at " + lat.toFixed(5) + "," + lng.toFixed(5) + " — " + base); } catch (e) {}
      } catch (err) {
        lastErr = err;
        try { console.warn("[parcel] " + lbl + " query failed: " + String(err && err.message || err) + " — " + base); } catch (e) {}
      }
    }
    // every listed source failed → the configured county layer is the last live
    // hope (it only answers inside its county; a miss costs one request)
    if (!reachedEmpty) {
      const countyBase = String(config.countyUrl || DEFAULT_COUNTY_URL || "").trim().replace(/\/+$/, "");
      if (countyBase && bases.indexOf(countyBase) === -1) {
        try {
          const res2 = await lookupAt(countyBase, lat, lng, address, wantNum, "County GIS — statewide sources were down, county answered", "county-live");
          if (res2) return res2;
          reachedEmpty = true; emptyLabel = "County GIS";
        } catch (e2) { /* all layers down → placeholder below, loudly flagged */ }
      }
    }
    if (reachedEmpty) return Object.assign(fallback(lat, lng, address), { providerStatus: "none", noCoverage: true, provider: emptyLabel + " — no parcel here" });
    // nothing answered -> offline placeholder, carrying the why for the banner
    return Object.assign(fallback(lat, lng, address), { error: String(lastErr && (lastErr.message || lastErr) || "no parcel source answered") });
  }

  // one-click source validation for Settings: query a known Green Bay point
  // against ANY layer URL and report exactly what came back — so a working
  // parcel source is verifiable in seconds, without redeploying anything.
  async function testSource(url, lat, lng) {
    const base = String(url || "").trim().replace(/\/+$/, "");
    if (!base) return { ok: false, error: "No URL to test" };
    resolveDeadline = Date.now() + 12000;
    try {
      let fc = await queryArc(base, lat != null ? lat : 44.5213, lng != null ? lng : -88.0241, 30);
      const feats = (fc && fc.features) || [];
      const feat = feats.find(f => outerRing(f.geometry));
      if (!feat) return { ok: false, error: "Layer answered but returned no parcel at the test point" };
      const meta = parseProps(feat.properties || {});
      return { ok: true, count: feats.length, parcelId: meta.parcelId || "—", owner: meta.owner || "—" };
    } catch (e) {
      return { ok: false, error: String(e && (e.message || e)) };
    } finally { resolveDeadline = 0; }
  }

  window.VFP_PARCEL = { resolve, geocode, suggest, setConfig, getConfig, testSource, DEFAULT_COUNTY_URL, WI_BASE };
})();
